import {
  EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES,
  getSignalCategoriesForTargetType,
  INTERNAL_NETWORK_SIGNAL_CATEGORIES,
  type SignalCategoryType,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  LIST_BADGE_LIFECYCLE_STATE,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { omit } from 'radash';
import { resolveBlockedSignalFilters } from '@terra/shared-contracts-signals';
import type { SignalFilters } from '../../../hooks/useSignals';
import type { SignalCountsFilters } from '../../../hooks/useSignalCounts';
import type { AiComponentSignalCountsFilters } from '../../../hooks/api/useAiComponentSignalCounts';
import { toSignalsOrderBy } from './signals-list.model';
import type {
  SignalExecutionFilter,
  useTabScopedSignalsFilters,
} from '../../../store/tab/signalsTabScopedFilterStore';

type SignalsFilterStore = ReturnType<typeof useTabScopedSignalsFilters>;

export type SignalsFilterValues = Pick<
  SignalsFilterStore,
  | 'searchTerm'
  | 'urlSearchTerm'
  | 'stageFilter'
  | 'statusFilter'
  | 'signalStatusFilter'
  | 'confirmedVulnerabilityFindingStateFilter'
  | 'blockedReasonFilter'
  | 'confidenceFilter'
  | 'categoryFilter'
  | 'originFilter'
  | 'sourceFilter'
  | 'assigneeFilter'
  | 'hasExecutionFilter'
  | 'hasFindingFilter'
  | 'securityTestFilter'
  | 'appWideFilter'
  | 'createdStartDate'
  | 'createdEndDate'
  | 'testedStartDate'
  | 'testedEndDate'
  | 'sortField'
  | 'sortOrder'
  | 'grouping'
>;

export type SignalsListScope = Pick<
  SignalCountsFilters,
  'endpointIds' | 'urlPatterns' | 'componentTypes'
> & {
  includeApplicationLevelGroup?: boolean;
};

export type ResolvedSignalCategoryFilter = 'ALL' | SignalCategoryType[];

export const toTriStateBoolean = (
  value: SignalExecutionFilter,
): boolean | undefined => (value === 'ALL' ? undefined : value === 'WITH');

const toNonEmpty = <TValue>(
  values: readonly TValue[] | undefined,
): TValue[] | undefined =>
  values && values.length > 0 ? [...values] : undefined;

const allowedCategoriesForTarget = ({
  targetType,
  isNetworkTarget,
}: {
  targetType: TargetTypeType | null | undefined;
  isNetworkTarget: boolean;
}): SignalCategoryType[] => {
  if (isNetworkTarget) {
    return targetType === 'INTERNAL_NETWORK'
      ? [...INTERNAL_NETWORK_SIGNAL_CATEGORIES]
      : [...EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES];
  }

  return [
    ...getSignalCategoriesForTargetType(
      targetType === 'AI_APPLICATION' ? 'AI_APPLICATION' : 'WEB_APPLICATION',
    ),
  ];
};

export const resolveSignalCategoryFilter = ({
  targetType,
  isNetworkTarget,
  categoryFilter,
}: {
  targetType: TargetTypeType | null | undefined;
  isNetworkTarget: boolean;
  categoryFilter: readonly SignalCategoryType[];
}): ResolvedSignalCategoryFilter => {
  if (categoryFilter.length === 0) {
    return 'ALL';
  }

  const allowedCategories = allowedCategoriesForTarget({
    targetType,
    isNetworkTarget,
  });
  const allowedSet = new Set<string>(allowedCategories);
  const scopedCategories = categoryFilter.filter(
    (category): category is SignalCategoryType => allowedSet.has(category),
  );

  return scopedCategories.length === 0 ? 'ALL' : scopedCategories;
};

const resolveSignalStatusFilters = (values: SignalsFilterValues) =>
  resolveBlockedSignalFilters({
    status: toNonEmpty(values.signalStatusFilter),
    blockedReason: toNonEmpty(values.blockedReasonFilter),
  });

export const buildSignalListFilters = ({
  values,
  resolvedCategoryFilter,
  lifecycleQueryParam,
  isRejectedView,
}: {
  values: SignalsFilterValues;
  resolvedCategoryFilter: ResolvedSignalCategoryFilter;
  lifecycleQueryParam: LifecycleStateFilterDto | undefined;
  isRejectedView: boolean;
}): SignalFilters => {
  const sort = { field: values.sortField, order: values.sortOrder };

  if (isRejectedView) {
    return {
      stage: 'ALL',
      status: 'ALL',
      description: values.searchTerm,
      confidence: [],
      category: [],
      origin: [],
      assigneeId: [],
      url: '',
      orderBy: [sort],
    };
  }

  const statusFilters = resolveSignalStatusFilters(values);
  const orderBy = toSignalsOrderBy({ sort, grouping: values.grouping });

  return {
    stage: values.stageFilter.length === 0 ? 'ALL' : values.stageFilter,
    status: statusFilters.status?.length ? statusFilters.status : 'ALL',
    confirmedVulnerabilityFindingState: toNonEmpty(
      values.confirmedVulnerabilityFindingStateFilter,
    ),
    blockedReason: statusFilters.blockedReason,
    description: values.searchTerm,
    confidence: values.confidenceFilter,
    category: resolvedCategoryFilter,
    origin: values.originFilter.length === 0 ? 'ALL' : values.originFilter,
    source: toNonEmpty(values.sourceFilter),
    assigneeId: values.assigneeFilter,
    url: values.urlSearchTerm,
    hasExecution: toTriStateBoolean(values.hasExecutionFilter),
    hasFinding: toTriStateBoolean(values.hasFindingFilter),
    hasSecurityTestCase: toTriStateBoolean(values.securityTestFilter),
    isAppWide: toTriStateBoolean(values.appWideFilter),
    createdStartDate: values.createdStartDate,
    createdEndDate: values.createdEndDate,
    testedStartDate: values.testedStartDate,
    testedEndDate: values.testedEndDate,
    orderBy,
    ...(values.grouping && { groupBy: values.grouping.field }),
    lifecycleState: lifecycleQueryParam,
  };
};

export const buildSignalScopeCountsFilters = ({
  scope,
  lifecycleState,
}: {
  scope: SignalsListScope;
  lifecycleState: LifecycleStateFilterDto | undefined;
}): SignalCountsFilters => ({
  endpointIds: toNonEmpty(scope.endpointIds),
  componentTypes: scope.componentTypes,
  urlPatterns: toNonEmpty(scope.urlPatterns),
  lifecycleState,
});

export const buildSignalTotalCountsFilters = (
  scope: SignalsListScope,
): SignalCountsFilters =>
  buildSignalScopeCountsFilters({
    scope,
    lifecycleState: LIST_BADGE_LIFECYCLE_STATE,
  });

export const buildSignalCountsFilters = ({
  values,
  resolvedCategoryFilter,
  lifecycleQueryParam,
  isRejectedView,
  scope,
}: {
  values: SignalsFilterValues;
  resolvedCategoryFilter: ResolvedSignalCategoryFilter;
  lifecycleQueryParam: LifecycleStateFilterDto | undefined;
  isRejectedView: boolean;
  scope: SignalsListScope;
}): SignalCountsFilters => {
  if (isRejectedView) {
    return {
      description: values.searchTerm || undefined,
      rejectedOnly: true,
    };
  }

  const statusFilters = resolveSignalStatusFilters(values);

  return {
    description: values.searchTerm || undefined,
    stage: toNonEmpty(values.stageFilter),
    status: statusFilters.status,
    confirmedVulnerabilityFindingState: toNonEmpty(
      values.confirmedVulnerabilityFindingStateFilter,
    ),
    blockedReason: statusFilters.blockedReason,
    confidence: toNonEmpty(values.confidenceFilter),
    ...(resolvedCategoryFilter !== 'ALL'
      ? { category: resolvedCategoryFilter }
      : {}),
    origin: toNonEmpty(values.originFilter),
    source: toNonEmpty(values.sourceFilter),
    url: values.urlSearchTerm || undefined,
    assigneeId: toNonEmpty(values.assigneeFilter),
    hasExecution: toTriStateBoolean(values.hasExecutionFilter),
    hasFinding: toTriStateBoolean(values.hasFindingFilter),
    hasSecurityTestCase: toTriStateBoolean(values.securityTestFilter),
    isAppWide: toTriStateBoolean(values.appWideFilter),
    createdStartDate: values.createdStartDate,
    createdEndDate: values.createdEndDate,
    testedStartDate: values.testedStartDate,
    testedEndDate: values.testedEndDate,
    ...buildSignalScopeCountsFilters({
      scope,
      lifecycleState: lifecycleQueryParam,
    }),
  };
};

const toOptionalDate = (value: Date | null | undefined): Date | undefined =>
  value ?? undefined;

export const buildAiKindSignalCountsFilters = (
  countsFilters: SignalCountsFilters,
): AiComponentSignalCountsFilters => {
  const filters = omit(countsFilters, [
    'componentTypes',
    'urlPatterns',
    'assetIds',
    'endpointIds',
  ]);

  return {
    ...filters,
    createdStartDate: toOptionalDate(filters.createdStartDate),
    createdEndDate: toOptionalDate(filters.createdEndDate),
    testedStartDate: toOptionalDate(filters.testedStartDate),
    testedEndDate: toOptionalDate(filters.testedEndDate),
  };
};

export const hasActiveSignalListFilters = (
  values: SignalsFilterValues,
): boolean =>
  values.searchTerm !== '' ||
  values.urlSearchTerm !== '' ||
  values.stageFilter.length > 0 ||
  values.statusFilter.length > 0 ||
  values.confidenceFilter.length > 0 ||
  values.categoryFilter.length > 0 ||
  values.originFilter.length > 0 ||
  values.sourceFilter.length > 0 ||
  values.assigneeFilter.length > 0 ||
  values.hasExecutionFilter !== 'ALL' ||
  values.hasFindingFilter !== 'ALL' ||
  values.securityTestFilter !== 'ALL' ||
  values.appWideFilter !== 'ALL' ||
  values.createdStartDate !== null ||
  values.createdEndDate !== null ||
  values.testedStartDate !== null ||
  values.testedEndDate !== null;

export const isSignalLifecycleFilterActive = ({
  showLifecycleSelector,
  lifecycleState,
}: {
  showLifecycleSelector: boolean;
  lifecycleState: LifecycleStateFilterDto;
}): boolean =>
  showLifecycleSelector && lifecycleState !== DEFAULT_LIFECYCLE_STATE_FILTER;

export const hasActiveSignalAdvancedFilters = ({
  values,
  isNetworkTarget,
}: {
  values: SignalsFilterValues;
  isNetworkTarget: boolean;
}): boolean =>
  values.confidenceFilter.length > 0 ||
  values.originFilter.length > 0 ||
  values.sourceFilter.length > 0 ||
  values.assigneeFilter.length > 0 ||
  values.hasExecutionFilter !== 'ALL' ||
  values.hasFindingFilter !== 'ALL' ||
  values.testedStartDate !== null ||
  values.testedEndDate !== null ||
  (!isNetworkTarget &&
    (values.urlSearchTerm !== '' || values.securityTestFilter !== 'ALL'));

const toSortedValues = (values: readonly string[] | undefined) =>
  [...(values ?? [])].sort();

export const buildSignalsSelectionScopeKey = ({
  targetId,
  targetType,
  endpointId,
  filters,
  scope,
  isRejectedView,
}: {
  targetId: string | null | undefined;
  targetType: string | null | undefined;
  endpointId: string | undefined;
  filters: SignalFilters;
  scope: SignalsListScope;
  isRejectedView: boolean;
}): string =>
  JSON.stringify({
    targetId,
    targetType,
    endpointId,
    filters,
    endpointIds: toSortedValues(scope.endpointIds),
    componentTypes: toSortedValues(scope.componentTypes),
    urlPatterns: toSortedValues(scope.urlPatterns),
    includeApplicationLevelGroup: scope.includeApplicationLevelGroup,
    isRejectedView,
  });
